<script setup lang="ts">
import { Paperclip } from 'lucide-vue-next';
import { isFormUploadedAnswerValueArray } from '~/lib/formUploads';
import type { FormSubmissionAnswer } from '~/types/models';

const props = withDefaults(defineProps<{
    answers: FormSubmissionAnswer[];
    emptyMessage?: string;
    surface?: 'default' | 'plain';
}>(), {
    emptyMessage: 'No answers were saved for this submission.',
    surface: 'default',
});

const listClass = computed(() => props.surface === 'plain'
    ? 'divide-y divide-border/40'
    : 'divide-y divide-border/40 rounded-2xl border border-border/70 bg-card/70');
const itemClass = computed(() => props.surface === 'plain'
    ? 'py-4'
    : 'px-4 py-4 sm:px-5');

function textSummary(value: FormSubmissionAnswer['value']): string {
    if (Array.isArray(value)) {
        if (isFormUploadedAnswerValueArray(value)) {
            return '';
        }
        return value.length > 0 ? value.join(', ') : 'No response';
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    const normalized = value == null ? '' : String(value).trim();
    return normalized || 'No response';
}
</script>

<template>
    <div v-if="answers.length === 0" class="rounded-xl border border-dashed border-border/70 px-4 py-5 text-sm text-muted-foreground">
        {{ emptyMessage }}
    </div>

    <div v-else :class="listClass">
        <div
            v-for="answer in answers"
            :key="answer.fieldId"
            :class="itemClass"
        >
            <p class="text-sm font-medium text-foreground">{{ answer.fieldLabel || 'Question' }}</p>

            <div v-if="isFormUploadedAnswerValueArray(answer.value)" class="mt-2 flex flex-wrap gap-2">
                <a
                    v-for="file in answer.value"
                    :key="`${answer.fieldId}-${file.fileURL}`"
                    :href="file.fileURL"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-3 py-1.5 text-sm text-foreground transition hover:bg-accent"
                >
                    <Paperclip class="size-3.5 text-muted-foreground" />
                    <span>{{ file.fileName }}</span>
                </a>
            </div>

            <p v-else class="mt-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                {{ textSummary(answer.value) }}
            </p>
        </div>
    </div>
</template>
